/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { useMemo } from 'react';
import TabPanel from '@mui/lab/TabPanel';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';

import { useAppSelector } from '../../../../../app/hooks';
import { DynamedTabContentPage } from '../../../DynaMed/DynamedTabContentPage';
import { DYNAMED_PAGE_ID, DYNAMED_TAB_VALUE, hasDynamedGuide } from '../../../DynaMed/dynamed';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import { useDynamedTabUserFlag } from '../../../permissions';
import { addToArray } from '../../../utils';
import {
  selectCreatorMode,
  selectDocumentVersionById,
  selectIsSearching,
  selectSelectedKeysForMessage,
} from '../../chatSlice';
import { MessageIndexProvider, PageProviderByPageName, useMessageIndex, usePage } from '../../hooks/MessageContext';
import styles from '../../Message.module.scss';
import {
  getKeyForExclusiveQuestion,
  getKeyForMultipleChoiceOption,
  getKeyForQuestionOther,
  getValueForExclusiveOption,
  getValueForMultipleChoiceOption,
} from '../../../algorithm/question_utils';
import {
  PageTypeEnum,
  PageType,
  PageCalculatorType,
  PageMultipleQuestionsType,
  PageOptionExclusiveType,
  SelectedButtonInfo,
} from '../../../algorithm/types';
import { OptionsButton } from '../OptionsButton';
import { RenderedHTML } from '../RenderedHtml/RenderedHTML';
import { TooltipIcon } from '../TooltipIcon';
import { InnerMessage } from './InnerMessage';
import { InnerMessageFooter } from './InnerMessageFooter';
import { InnerMessageHeader } from './InnerMessageHeader';
import { getNextPageLinks, questionCanHaveToggleableRequired } from '../../../algorithm/runAlgorithm';
import { CalculatorPageFooter } from './CalculatorPageFooter';
import { questionTypeAsString } from './utils';
import { PageVignettesJsonType, VignettesJsonFileType } from '../../../algorithm/vignettes_types';
import { VignettesPage } from '../../../vignettes/VignettesPage';
import { SelectKeysSummaryTable } from './SelectKeysSummaryTable';
import { analyticsTrack } from '../../../analytics_utils';
import { DocumentTocGuidePage } from './DocumentTocGuidePage';
import { COMBINED_GUIDE_PAGE_ID } from './documentTocGuidePageConstants';

type PageProps = {
  onKeyToggled: (page: PageType, key: string, value: string) => void;
  onShowTooltip: (element: HTMLElement, message: string | undefined, header: string | undefined) => void;
  allowEditing?: boolean;
  loopCheckPageTitles?: string[]; // Check for loops when including a page in a page
  loopCheckPageTitlesNextPage?: string;
  readOnly: boolean;
  readOnlyForContextualization: boolean;
  flatMode?: boolean; // Don't show the next page, or swimlane summary - this is for editting mode
};

function getAlgorithmStepNumber(
  selectedKeys: { [key: string]: SelectedButtonInfo } | undefined,
  questionkey: string
): number {
  const selectedKeyCount = Object.keys(selectedKeys ?? {}).length;
  const currentQuestionAlreadySelected = !!selectedKeys?.[questionkey.toUpperCase()];
  return currentQuestionAlreadySelected ? selectedKeyCount : selectedKeyCount + 1;
}

/**
 * This is the readonly version of a Page.
 * @see PageFlipped for the flipped version of this page
 */
export function Page({
  onKeyToggled,
  onShowTooltip,
  allowEditing,
  loopCheckPageTitles: loopCheckPageTitles_,
  loopCheckPageTitlesNextPage,
  readOnly,
  readOnlyForContextualization,
  flatMode, // Don't show the next page, or swimlane summary - this is for editting mode
}: PageProps) {
  const page = usePage().page!;
  const messageIndex = useMessageIndex();
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, page.document_version_id));
  const isSearching = useAppSelector(selectIsSearching);
  const isCreatorMode = useAppSelector(selectCreatorMode);
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const dynamedTabEnabled = useDynamedTabUserFlag();
  const patient_notes_summary = pearlUserInfo?.userFlags?.patient_notes_summary;
  // We have to get this for the current message, not the current state.selected see PRL-2861
  // But for swimlanes it should be the previous messageIndex so that pages embedded in the swimlane
  // output don't affect the swimlane itself. See PRL-2947
  const selectedKeys = useAppSelector((state) =>
    selectSelectedKeysForMessage(state, page.pageType === PageTypeEnum.SWIMLANE ? messageIndex - 1 : messageIndex)
  );
  const params = new URLSearchParams(window.location.search);
  const slug = params.get('slug');
  // Call the track func only for this algo.
  const shouldTrackDocument =
    slug === 'antenatal_corticosteroid_acs_blue_box_program' ||
    slug === 'antenatal_corticosteroid_acs_blue_box_program_testing' ||
    slug === 'antenatal_corticosteroid_blue_box_program';

  const loopCheckPageTitles = useMemo(
    () => addToArray(loopCheckPageTitles_, loopCheckPageTitlesNextPage),
    [loopCheckPageTitlesNextPage, loopCheckPageTitles_]
  );
  function trackOptionKeyToggled(
    page: PageMultipleQuestionsType | PageCalculatorType,
    questionkey: string,
    optionKey: string
  ) {
    const runAlgorithmUniqueId = sessionStorage.getItem('algorithmRunReferrenceId');
    if (!runAlgorithmUniqueId) return;
    const question = page?.questions?.find((q) => q.id === questionkey);
    let option_: PageOptionExclusiveType | undefined;
    if (question?.type === 'exclusive') {
      option_ = question.options.find((o) => o.exclusive_value === optionKey);
    }
    // The selected keys is not set in the very first time, it always has the value of the previous selection.
    // This is a quick work-around. Would set up the index accordingly.
    const isFirstForRun = selectedKeys && Object.keys(selectedKeys).length === 0;
    const eventProps = {
      questionText: question?.question || page.name,
      choice: option_?.text,
      algorithm_run_id: runAlgorithmUniqueId,
      start_page: isFirstForRun,
      step_number: isFirstForRun ? 1 : getAlgorithmStepNumber(selectedKeys, questionkey),
      documentVersionId: page.document_version_id,
      documentId: documentVersion?.document.id,
      documentSlug: documentVersion?.document.slug,
    };

    analyticsTrack('algo_question_toggled', { ...eventProps }, pearlUserInfo);
  }

  if (loopCheckPageTitles.includes(page.name)) {
    return <div className={styles.pageError}>Include loops not allowed</div>;
  }

  switch (page.pageType) {
    case PageTypeEnum.MULTIPLE_QUESTIONS:
    case PageTypeEnum.CALCULATOR:
      if (page.questions?.length! > 0) {
        // only set justOne if there is exactly one question and it has no text for the question
        const justOne = page.questions?.length === 1 && !page.questions[0]?.question;
        return (
          <div className={`${styles.questions} ${justOne ? styles.oneQuestion : undefined}`}>
            {page.questions?.map((question, questionIndex) => {
              const typeStr = questionTypeAsString(question);
              const title = !justOne ? (
                <div className={styles.question_title}>
                  <RenderedHTML
                    content={question.questionHtml || ''}
                    documentVersionId={page?.document_version_id}
                    pageId={page?.id}
                    creatorMode={!!allowEditing}
                    documentId={documentVersion?.document?.id}
                    documentSlug={documentVersion?.document?.slug}
                    pageName={page?.name}
                    inlineDiv
                  />
                  {questionCanHaveToggleableRequired(question) && question.required ? (
                    <span className={styles.required}>*</span>
                  ) : undefined}
                  <TooltipIcon
                    onTooltipClick={onShowTooltip}
                    tooltip={question.tooltip}
                    pageId={page.id}
                    tooltipHeader={(question as any).question}
                    className={styles.question_tooltip}
                  />
                  {typeStr && <span className={styles.question_type}>{typeStr}</span>}
                </div>
              ) : (
                typeStr && (
                  <div className={styles.question_title_type_only}>
                    <span className={styles.question_type}>{typeStr}</span>
                  </div>
                )
              );
              const hasLongButtonText =
                (question.type === 'exclusive' || question.type === 'multiple_choice') &&
                question.options?.some((option) => option.text.length > 40);
              if (question.type === 'exclusive') {
                const key = getKeyForExclusiveQuestion(question, page);
                return (
                  <div className={styles.question} key={questionIndex}>
                    {title}
                    <ToggleButtonGroup
                      exclusive
                      value={selectedKeys?.[key]}
                      className={hasLongButtonText ? styles.buttongroupLarge : styles.buttongroup}
                    >
                      {question.options.map((option, optionIndex) => {
                        const value = getValueForExclusiveOption(option);
                        return (
                          <OptionsButton
                            option={option}
                            selected={selectedKeys?.[key]?.value === value}
                            onClick={() => {
                              onKeyToggled(page, key, getValueForExclusiveOption(option));
                              if (shouldTrackDocument && !isCreatorMode)
                                trackOptionKeyToggled(page, key, getValueForExclusiveOption(option));
                            }}
                            onTooltipClick={onShowTooltip}
                            fhir={option.fhirWidgetId ? page.fhir?.idsToFhir[option.fhirWidgetId] : undefined}
                            key={optionIndex}
                            buttonKey={key}
                            buttonValue={value}
                            questionIndex={questionIndex}
                            optionIndex={optionIndex}
                            showLinks={flatMode} // In flat mode we want to see which options link to other pages
                            points={
                              page.pageType === PageTypeEnum.CALCULATOR && !page.hideScores ? option.points : undefined
                            }
                            // option can be linked to another algorithm, so disabling the option btn till the algorithm search api call is completed to avoid race condition b/w options.
                            isDisabled={isSearching}
                            documentVersionId={page?.document_version_id}
                          >
                            {option.text}
                          </OptionsButton>
                        );
                      })}
                    </ToggleButtonGroup>
                  </div>
                );
              } else if (question.type === 'multiple_choice') {
                return (
                  <div className={styles.question} key={questionIndex}>
                    {title}
                    <ToggleButtonGroup
                      className={hasLongButtonText ? styles.buttongroupLarge : styles.buttongroup}
                      value={
                        selectedKeys ? Object.keys(selectedKeys).map((key) => `${key}#${selectedKeys?.[key]}`) : []
                      }
                    >
                      {question.options?.map((option, optionIndex) => {
                        const key = getKeyForMultipleChoiceOption(option);
                        const value = getValueForMultipleChoiceOption(option);
                        return (
                          <OptionsButton
                            option={option}
                            selected={selectedKeys?.[key]?.value === value}
                            onClick={() => {
                              onKeyToggled(page, key, getValueForMultipleChoiceOption(option));
                            }}
                            onTooltipClick={onShowTooltip}
                            fhir={option.fhirWidgetId ? page.fhir?.idsToFhir[option.fhirWidgetId] : undefined}
                            key={optionIndex}
                            buttonKey={key}
                            buttonValue={value}
                            showLinks={flatMode} // In flat mode we want to see which options link to other pages
                            questionIndex={questionIndex}
                            optionIndex={optionIndex}
                            points={
                              page.pageType === PageTypeEnum.CALCULATOR && !page.hideScores ? option.points : undefined
                            }
                            documentVersionId={page?.document_version_id}
                          >
                            {option.text}
                          </OptionsButton>
                        );
                      })}
                    </ToggleButtonGroup>
                  </div>
                );
              } else {
                const key = getKeyForQuestionOther(question);
                const value = selectedKeys?.[key]?.value;
                return (
                  <div className={styles.question} key={questionIndex}>
                    {title}
                    <div style={{ padding: '0 10px 10px 20px' }}>
                      {question.type === 'free_text' && (
                        <textarea
                          defaultValue={value ?? ''}
                          placeholder={question.required ? 'Required free text' : 'Optional free text'}
                          style={{ width: '100%', height: 100 }}
                          data-key={key /* Just for debug help */}
                          data-value={value}
                          onBlur={(e) => onKeyToggled(page, key, e.target.value)}
                        />
                      )}
                      {question.type === 'numerical_entry' && (
                        <input
                          placeholder={question.required ? 'Required number' : 'Optional number'}
                          defaultValue={value ?? ''}
                          type="number"
                          min={question.min}
                          max={question.max}
                          data-key={key /* Just for debug help */}
                          data-value={value}
                          step={question.step || 0.1}
                          style={{ width: 200 }}
                          onBlur={(e) => onKeyToggled(page, key, e.target.value)}
                        />
                      )}
                      {question.type === 'upload_picture' && (
                        <input type="file" accept="image/*" style={{ width: 200 }} />
                      )}
                    </div>
                  </div>
                );
              }
            })}
            {page.pageType === PageTypeEnum.CALCULATOR && (
              // Make a table at the bottom of the calculator page summarizing the selected buttons,
              // along with any 'comment' (e.g. "score = 2.0")
              <CalculatorPageFooter page={page} />
            )}
            {page.isRoot && <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />}
          </div>
        );
      } else {
        // Has no questions
        const { pageLinks, error } = getNextPageLinks(page, -1, selectedKeys);
        return (
          <>
            {error && <div style={{ color: 'gray', fontStyle: 'italic' }}>{error}</div>}
            {pageLinks?.map((next_link, index) => (
              <div style={{ position: 'relative' }} key={index}>
                {flatMode ? (
                  <i>
                    Includes: <h2>{next_link}</h2>
                  </i>
                ) : (
                  <MessageIndexProvider value={messageIndex + 0.01 * index}>
                    <PageProviderByPageName page_name={next_link} document_version_id={page.document_version_id}>
                      <InnerMessage
                        document_version_id={page.document_version_id}
                        onKeyToggled={onKeyToggled}
                        onShowTooltip={onShowTooltip}
                        allowEditing={allowEditing}
                        loopCheckPageTitles={loopCheckPageTitles}
                        loopCheckPageTitlesNextPage={page.name}
                      />
                    </PageProviderByPageName>
                  </MessageIndexProvider>
                )}
              </div>
            ))}
          </>
        );
      }
    case PageTypeEnum.TABS:
      /* Note that the Page must be wrapped in a TabPageContainer which has the TabContext */
      return (
        <>
          {page.options?.map((option, index) => {
            /* Note we purposefully do not have a relative div here, because we want the edit badge to be relative to the overall tab page */
            const isCombinedGuidePage = page.id === 'LANDING' && option.id === COMBINED_GUIDE_PAGE_ID;
            return (
              <TabPanel key={index} className={styles.tabsPanel} value={index.toString()}>
                <MessageIndexProvider value={messageIndex + 0.01}>
                  {isCombinedGuidePage && documentVersion ? (
                    <>
                      <InnerMessageHeader documentVersion={documentVersion} isFlipped={false} isRoot={true} />
                      <DocumentTocGuidePage allowEditing={allowEditing} documentVersion={documentVersion} />
                      <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />
                    </>
                  ) : (
                    <PageProviderByPageName
                      page_name={option.link || option.text}
                      document_version_id={page.document_version_id}
                    >
                      <InnerMessageHeader documentVersion={documentVersion} isFlipped={false} isRoot={true} />
                      <InnerMessage
                        document_version_id={page.document_version_id}
                        onKeyToggled={onKeyToggled}
                        onShowTooltip={onShowTooltip}
                        allowEditing={allowEditing}
                        loopCheckPageTitles={loopCheckPageTitles}
                        loopCheckPageTitlesNextPage={page.name}
                      />
                      {option.text !== 'Algorithm' && (
                        /* The algorithm will show the InnerMessageFooter in the PageTypeEnum.MULTIPLE_QUESTIONS above */
                        <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />
                      )}
                    </PageProviderByPageName>
                  )}
                </MessageIndexProvider>
              </TabPanel>
            );
          })}
          {dynamedTabEnabled && hasDynamedGuide(documentVersion?.title) && (
            <TabPanel className={styles.tabsPanel} value={DYNAMED_TAB_VALUE}>
              <MessageIndexProvider value={messageIndex + 0.01}>
                <InnerMessageHeader documentVersion={documentVersion} isFlipped={false} isRoot={true} />
                <DynamedTabContentPage
                  allowEditing={allowEditing}
                  documentId={documentVersion?.document?.id}
                  documentSlug={documentVersion?.document?.slug}
                  documentVersionId={page.document_version_id}
                  guideName={documentVersion?.title}
                  pageId={DYNAMED_PAGE_ID}
                />
                <InnerMessageFooter documentVersion={documentVersion} className={styles.tabFooter} />
              </MessageIndexProvider>
            </TabPanel>
          )}
        </>
      );
    case PageTypeEnum.SWIMLANE: {
      if (!selectedKeys || Object.keys(selectedKeys).length === 0) {
        return (
          <div style={{ color: 'gray' }}>
            <b>Swimlane:</b> {page.name}
          </div>
        );
      }
      const { pageLinks, laneContents, error } = getNextPageLinks(page, -1, selectedKeys);
      return (
        <>
          {!flatMode && !page.hide_summary && (
            <div
              data-id="swimlane-output"
              className={`${styles.laneSummary} ${!isCreatorMode && patient_notes_summary && styles.gapForCopyButton}`}
            >
              <SelectKeysSummaryTable messageIndex={messageIndex - 1} />
            </div>
          )}
          {error && (
            <div style={{ color: 'gray' }}>
              <br />
              <b>{error}</b>
            </div>
          )}
          {!flatMode &&
            laneContents?.map((content, index) => (
              /* Found a matching swim lane */
              <div key={index} className={styles.laneOutput}>
                <RenderedHTML
                  content={content || ''}
                  documentVersionId={page?.document_version_id}
                  documentId={documentVersion?.document?.id}
                  pageId={page?.id}
                  creatorMode={!!allowEditing}
                  documentSlug={documentVersion?.document?.slug}
                  pageName={page?.name}
                />
              </div>
            ))}
          {pageLinks?.map((link, index) => (
            /* Found a matching swim lane with a link!! */
            /* Note we need a relative div so the edit badge is in the right place */
            /* Note, for external links (starting with '#') we deal with these in
             * the runPage */
            <div key={`link-${index}`} style={{ position: 'relative', marginTop: 40 }}>
              {flatMode ? (
                <i>
                  Includes: <h2>{link}</h2>
                </i>
              ) : (
                <MessageIndexProvider value={messageIndex + 0.01 * index}>
                  <PageProviderByPageName page_name={link} document_version_id={page.document_version_id}>
                    <InnerMessage
                      document_version_id={page.document_version_id}
                      onKeyToggled={onKeyToggled}
                      onShowTooltip={onShowTooltip}
                      allowEditing={allowEditing}
                      loopCheckPageTitles={loopCheckPageTitles}
                      loopCheckPageTitlesNextPage={page.name}
                    />
                  </PageProviderByPageName>
                </MessageIndexProvider>
              )}
            </div>
          ))}
        </>
      );
    }
    case PageTypeEnum.JSON:
      if (page.schema && page.schema !== 'vignettes') {
        return <div>JSON page with schema: {page.schema}</div>;
      }
      const json = (page.json || {}) as VignettesJsonFileType;
      return <VignettesPage page={page as PageVignettesJsonType} json={json} />;
    case PageTypeEnum.IF:
      const { pageLinks, error } = getNextPageLinks(page, -1, selectedKeys);
      // IF statement will only return one page, but treat it as a list anyway
      return (
        <>
          {error && <div style={{ color: 'gray', fontStyle: 'italic' }}>{error}</div>}
          {pageLinks?.map((next_link, index) => (
            <div style={{ position: 'relative' }} key={index}>
              <MessageIndexProvider value={messageIndex + 0.01 * index}>
                <PageProviderByPageName page_name={next_link} document_version_id={page.document_version_id}>
                  <InnerMessage
                    document_version_id={page.document_version_id}
                    onKeyToggled={onKeyToggled}
                    onShowTooltip={onShowTooltip}
                    allowEditing={allowEditing}
                    loopCheckPageTitles={loopCheckPageTitles}
                    loopCheckPageTitlesNextPage={page.name}
                  />
                </PageProviderByPageName>
              </MessageIndexProvider>
            </div>
          ))}
        </>
      );
    default:
      return <></>;
  }
}
